import React, { useEffect, useMemo, useState } from "react";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";

const createSchema = {
  name: [required("Role name is required.")],
};

/**
 * System create: name + allow.
 * Tenant create: name + permissions (create name, then update permissions by roleId).
 * Edit (both): permissions/allow only; name is read-only.
 */
const RoleFormDialog = ({
  open,
  mode = "create",
  role = null,
  allowSuggestions = [],
  permissionsLoading = false,
  loading = false,
  tenantMode = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const [formError, setFormError] = useState("");
  const [allow, setAllow] = useState([]);

  const { values, getFieldProps, validateAll, reset, isValid } =
    useFormValidation({ name: "" }, createSchema);

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({ name: role?.name || "" });
    setAllow(Array.isArray(role?.allow) ? [...role.allow] : []);
  }, [open, reset, role]);

  const options = useMemo(
    () =>
      Array.from(
        new Set([...(allowSuggestions || []), ...allow].filter(Boolean))
      ),
    [allow, allowSuggestions]
  );

  const canSubmit = Boolean(
    !loading &&
      (isCreate
        ? isValid && values.name?.trim() && allow.length > 0
        : allow.length > 0)
  );

  const handleSubmit = async () => {
    if (isCreate) {
      const { isValid: ok, errors } = validateAll();
      if (!ok) {
        setFormError(errors.name || "Please correct the highlighted fields.");
        return;
      }
    }

    if (!allow.length) {
      setFormError("Add at least one permission.");
      return;
    }

    setFormError("");
    const permissions = allow.filter(Boolean).map(String);
    try {
      if (isCreate) {
        if (tenantMode) {
          await onSubmit({
            name: values.name.trim(),
            permissions,
          });
        } else {
          await onSubmit({
            name: values.name.trim(),
            allow: permissions,
          });
        }
      } else {
        await onSubmit(
          tenantMode ? { permissions } : { allow: permissions }
        );
      }
    } catch (err) {
      setFormError(err?.message || "Unable to save role.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={isCreate ? "Add Role" : "Edit Role"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      disableSubmit={!canSubmit}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isCreate ? "Create Role" : "Save Changes"}
      loadingLabel="Saving..."
    >
      <FormField
        label="Role Name"
        required={isCreate}
        disabled={!isCreate || loading}
        {...getFieldProps("name")}
        value={isCreate ? values.name : role?.name || values.name || ""}
      />
      <ChipAutocomplete
        label="Permissions"
        required
        freeSolo
        options={options}
        value={allow}
        onChange={setAllow}
        loading={permissionsLoading}
        disabled={loading}
        helperText={
          tenantMode
            ? "Select permissions from the tenant permission list (or type a new one)."
            : "Example: idp.*.user.manage"
        }
      />
    </FormDialog>
  );
};

export default RoleFormDialog;
